import React, { useEffect, useState } from 'react';
import UserTable from '../components/UserTable';
import { api, endpoints } from '../config/api';
import { Alert, Spinner } from '../components/ui';
import { useT } from '../i18n';
import './Users.css';

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const messages = {
  docTitle: { tr: 'Personeller - Signature Manager', en: 'People - Signature Manager' },
  title: { tr: 'Personeller', en: 'People' },
  subtitle: {
    tr: ({ users, groups }) => `${users} personel · ${groups} grup`,
    en: ({ users, groups }) => `${plural(users, 'person', 'people')} · ${plural(groups, 'group', 'groups')}`,
  },
  search: { tr: 'İsim veya e-posta ara', en: 'Search by name or email' },
  groupAll: { tr: 'Grup: Tümü', en: 'Group: All' },
  loadError: { tr: 'Kullanıcılar alınamadı: {msg}', en: 'Couldn’t load users: {msg}' },
};

export default function Users() {
  const { t, lang } = useT(messages);
  useEffect(() => {
    document.title = t('docTitle');
  }, [t]);

  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [search, setSearch]   = useState('');
  const [groupFilter, setGroupFilter] = useState('');

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get(endpoints.users);
      setUsers(data);
    } catch (err) {
      console.error('Kullanıcılar alınamadı:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Bir kullanıcı birden fazla dizin grubuna dahil olabilir (users.groups) —
  // filtre seçenekleri ve eşleşme bu yüzden tek bir "birincil" gruba değil,
  // kullanıcının dahil olduğu tüm gruplara bakıyor.
  const groupOptions = Array.from(
    new Set(users.flatMap(u => (u.groups && u.groups.length > 0 ? u.groups : [u.group]).filter(Boolean)))
  ).sort((a, b) => a.localeCompare(b, lang));

  // Arama + grup filtresi
  const filteredUsers = users.filter(u => {
    const q = search.toLowerCase();
    const userGroups = u.groups && u.groups.length > 0 ? u.groups : [u.group].filter(Boolean);
    const matchesSearch = (
      (u.displayName && u.displayName.toLowerCase().includes(q)) ||
      (u.Mail && u.Mail.toLowerCase().includes(q)) ||
      (u.mail && u.mail.toLowerCase().includes(q)) ||
      userGroups.some(g => g.toLowerCase().includes(q))
    );
    const matchesGroup = !groupFilter || userGroups.includes(groupFilter);
    return matchesSearch && matchesGroup;
  });

  return (
    <div className="page-stack">
      <div className="users-header">
        <div>
          <h1>{t('title')}</h1>
          <p className="users-subtitle">{t('subtitle', { users: users.length, groups: groupOptions.length })}</p>
        </div>
      </div>

      <div className="users-filters">
        <div className="users-search">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" />
          </svg>
          <input
            type="text"
            placeholder={t('search')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {groupOptions.length > 0 && (
          <select className="users-group-filter" value={groupFilter} onChange={e => setGroupFilter(e.target.value)}>
            <option value="">{t('groupAll')}</option>
            {groupOptions.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        )}
      </div>

      {error && (
        <Alert variant="error">
          {t('loadError', { msg: error })}
        </Alert>
      )}
      {loading ? (
        <div className="page-loading"><Spinner /></div>
      ) : (
        <UserTable users={filteredUsers} />
      )}
    </div>
  );
}
